import React, { useState, useEffect } from 'react';
import { addTransactionAPI, getTransactionsForStudentAPI, deleteTransactionAPI, getTeachers, updateStudentAPI, sendReminderAPI, getSenderOptionsAPI } from './StudentAPI';

const StudentTransactionsModal = ({ isOpen, onClose, selectedStudent, transactions }) => {
    const [sortConfig, setSortConfig] = useState({ key: 'transaction_date', direction: 'ascending' });
    const [transactionList, setTransactionList] = useState(transactions);
    const [teachers, setTeachers] = useState([]);
    const [newTransaction, setNewTransaction] = useState({ transaction_date: '', amount: 0, teacher_id: '' });

    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState('');
    const [editedPhone, setEditedPhone] = useState('');
    const [editedEmail, setEditedEmail] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [displayPhone, setDisplayPhone] = useState('');
    const [displayEmail, setDisplayEmail] = useState('');
    const [reminderStatus, setReminderStatus] = useState(null);
    const [reminderError, setReminderError] = useState('');
    const [senderOptions, setSenderOptions] = useState([]);
    const [selectedSender, setSelectedSender] = useState('');

    useEffect(() => {
        setTransactionList(transactions);
    }, [transactions]);

    useEffect(() => {
        if (isOpen) {
            setDisplayName(selectedStudent.name);
            setDisplayPhone(selectedStudent.phone_num || '');
            setDisplayEmail(selectedStudent.email || '');
            setEditedName(selectedStudent.name);
            setEditedPhone(selectedStudent.phone_num || '');
            setEditedEmail(selectedStudent.email || '');
            setIsEditing(false);
            setReminderStatus(null);
            setReminderError('');
        }
    }, [isOpen, selectedStudent]);

    const handleSave = async () => {
        const updated = await updateStudentAPI(selectedStudent.student_id, editedName, editedPhone, editedEmail);
        if (updated) {
            setDisplayName(updated.name);
            setDisplayPhone(updated.phone_num || '');
            setDisplayEmail(updated.email || '');
        }
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditedName(displayName);
        setEditedPhone(displayPhone);
        setEditedEmail(displayEmail);
        setIsEditing(false);
    };

    useEffect(() => {
        const fetchTeachers = async () => {
            const allTeachers = await getTeachers();
            setTeachers(allTeachers);
        };
        if (isOpen) {
            fetchTeachers();
            getSenderOptionsAPI().then(opts => {
                setSenderOptions(opts);
                if (opts.length > 0) setSelectedSender(opts[0].email);
            });
        }
    }, [isOpen]);

    const getTeacherName = (teacherId) => {
        const teacher = teachers.find(t => t.id === teacherId);
        return teacher ? teacher.name : 'Unknown';
    };

    const sortedTransactions = [...transactionList].sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
    });

    const requestSort = key => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getArrow = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'ascending' ? '↑' : '↓';
        }
        return '';
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setNewTransaction(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const addTransaction = async () => {
        await addTransactionAPI(selectedStudent.student_id, newTransaction.amount, newTransaction.transaction_date, newTransaction.teacher_id);
        setNewTransaction({ transaction_date: '', amount: 0, teacher_id: null });
        const updatedTransactions = await getTransactionsForStudentAPI(selectedStudent.student_id);
        setTransactionList(updatedTransactions);
    };

    const deleteTransaction = async (transactionId) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this transaction?");
        if (!confirmDelete) {
            return;
        }
        try {
            await deleteTransactionAPI(transactionId);
            const updatedTransactions = await getTransactionsForStudentAPI(selectedStudent.student_id);
            setTransactionList(updatedTransactions);
        } catch (error) {
            console.error('Failed to delete transaction:', error);
        }
    };

    const balance = transactionList.reduce((sum, t) => sum + parseFloat(t.amount), 0);

    const handleSendReminder = async () => {
        setReminderStatus('sending');
        try {
            await sendReminderAPI(selectedStudent.student_id, selectedSender);
            setReminderStatus('success');
        } catch (err) {
            setReminderStatus('error');
            setReminderError(err.message || 'Unknown error');
        }
    };

    return (
        <div>
            <div className={`modal ${isOpen ? 'show' : ''}`} tabIndex="-1" role="dialog" style={isOpen ? { display: 'block' } : {}}>
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <div className="modal-title">
                                {isEditing ? (
                                    <div>
                                        <div className="form-group mb-1">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={editedName}
                                                onChange={e => setEditedName(e.target.value)}
                                                placeholder="Name"
                                            />
                                        </div>
                                        <div className="form-group mb-1">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={editedPhone}
                                                onChange={e => setEditedPhone(e.target.value)}
                                                placeholder="Phone"
                                            />
                                        </div>
                                        <div className="form-group mb-1">
                                            <input
                                                type="email"
                                                className="form-control"
                                                value={editedEmail}
                                                onChange={e => setEditedEmail(e.target.value)}
                                                placeholder="Parent Email"
                                            />
                                        </div>
                                        <button className="btn btn-primary btn-sm mr-2" onClick={handleSave}>Save</button>
                                        <button className="btn btn-secondary btn-sm" onClick={handleCancel}>Cancel</button>
                                    </div>
                                ) : (
                                    <div>
                                        <h5 className="mb-0">
                                            Transactions for Student: {displayName}
                                            <button className="btn btn-outline-secondary btn-sm ml-2" onClick={() => setIsEditing(true)}>Edit</button>
                                        </h5>
                                        <small className="text-muted d-block">Phone: {displayPhone || '—'}</small>
                                        <small className="text-muted d-block">Email: {displayEmail || '—'}</small>
                                    </div>
                                )}
                            </div>
                            <button type="button" className="close" data-dismiss="modal" aria-label="Close" onClick={onClose}>
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th onClick={() => requestSort('transaction_id')}>ID {getArrow('transaction_id')}</th>
                                        <th onClick={() => requestSort('transaction_date')}>Date {getArrow('transaction_date')}</th>
                                        <th onClick={() => requestSort('amount')}>Amount {getArrow('amount')}</th>
                                        <th>Teacher</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedTransactions.map(transaction => (
                                        <tr key={transaction.transaction_id}>
                                            <td>{transaction.transaction_id}</td>
                                            <td>{transaction.transaction_date.split('T')[0]}</td>
                                            <td>${transaction.amount}</td>
                                            <td>{getTeacherName(transaction.teacher_id)}</td>
                                            <td>
                                                <button className="btn btn-danger btn-sm" onClick={() => deleteTransaction(transaction.transaction_id)}>Delete</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <h5 className="mt-3">Add New Transaction</h5>
                            <div className="form-group">
                                <label>Date</label>
                                <input type="date" name="transaction_date" value={newTransaction.transaction_date} onChange={handleInputChange} className="form-control" />
                            </div>
                            <div className="form-group">
                                <label>Amount</label>
                                <input type="number" name="amount" value={newTransaction.amount} onChange={handleInputChange} className="form-control" />
                            </div>
                            <div className="form-group">
                                <label>Teacher</label>
                                <select name="teacher_id" value={newTransaction.teacher_id} onChange={handleInputChange} className="form-control">
                                    <option value="">Select a teacher</option>
                                    {teachers.map(teacher => (
                                        <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
                                    ))}
                                </select>
                            </div>
                            <button className="btn btn-primary" onClick={addTransaction}>Add Transaction</button>
                        </div>
                        <div className="modal-footer d-flex align-items-center">
                            <span className="mr-auto">
                                Balance: <strong>${balance.toFixed(2)}</strong>
                                {reminderStatus === 'success' && <span className="text-success ml-2">Reminder sent!</span>}
                                {reminderStatus === 'error' && <span className="text-danger ml-2">Failed: {reminderError}</span>}
                            </span>
                            <select
                                className="form-control form-control-sm mr-2"
                                style={{ width: 'auto' }}
                                value={selectedSender}
                                onChange={e => setSelectedSender(e.target.value)}
                                disabled={reminderStatus === 'sending'}
                            >
                                {senderOptions.map(opt => (
                                    <option key={opt.email} value={opt.email}>{opt.label}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className="btn btn-warning mr-2"
                                disabled={reminderStatus === 'sending' || !displayEmail}
                                onClick={handleSendReminder}
                            >
                                {reminderStatus === 'sending' ? 'Sending...' : 'Send Reminder'}
                            </button>
                            <button type="button" className="btn btn-secondary" data-dismiss="modal" onClick={onClose}>Close</button>
                        </div>
                    </div>
                </div>
            </div>
            {isOpen && <div className="modal-backdrop show"></div>}
        </div>
    );
}

export default StudentTransactionsModal;
